import type { LiveClockState } from './live-clock';
import { formatOddForDisplay } from './odds-format';
import { resolveEventRowLiveInfo } from './live-statistics';
import type { PlayerReward } from './rewards';
import { TicketType } from './tickets';

/** Default stake matching Figma quick-stake selection. */
export const DEFAULT_BETSLIP_STAKE = 10;

/** Fallback max tips when account settings are unavailable. */
export const DEFAULT_MAX_COMBI = 20;

/** Fallback minimum selections / fold floor for system bets. */
export const DEFAULT_MIN_SYSTEM = 2;

/** Fallback maximum selections allowed on a system ticket. */
export const DEFAULT_MAX_SYSTEM = 20;

export const BETSLIP_STORAGE_KEY = 'BetSlip';

export type BetslipMode = 'single' | 'combi' | 'system';

export interface BetslipEvent {
  EventId: string | number;
  Home: string;
  Away: string;
  HomeScore?: string;
  AwayScore?: string;
  Score?: string;
  /** Raw live match clock (e.g. `67'`), not a pre-formatted period+time label. */
  Time?: string;
  /** Live period code for WebsiteTranslates (e.g. `2HT`, `HalfTime`). */
  Period?: string;
  PeriodShort?: string;
  /** Extended match clock for client-side ticking (`mm:ss`). */
  TimeExtended?: string;
  Date?: string;
  Live?: boolean | string;
  Banked: boolean;
  Url?: string;
  Content?: string;
  Category?: string;
  Tournament?: string;
  TournamentId?: string | number;
  /** Classic SportsAPI `int?`. Lookup key for season validation — not the match EventId. */
  SeasonId?: number;
  /** Client-only slip label. Not on `TicketRequestEventInfo`. */
  SeasonName?: string;
  Outright?: boolean;
  Sport?: string;
  SportId?: string | number;
}

/** Nested leg inside a Bet Builder (`Custom`) package. */
export interface BetslipCustomSelection {
  EventId: string | number;
  MarketId: string | null;
  Market?: string | null;
  Outcome: string | null;
  OutcomeId?: string | null;
  OutcomeName?: string | null;
  Specifier?: string | null;
  SpecifierName?: string | null;
  SpecifierDescription?: Record<string, unknown> | string | null;
  Odd?: string | number | null;
  FeedType?: number;
  OddsIfVoided?: number | null;
}

export interface BetslipBet {
  MarketId: string | null;
  Market: string | null;
  Specifier?: string | null;
  SpecifierDescription?: Record<string, unknown> | string | null;
  Outcome: string | null;
  OutcomeId?: string | null;
  OutcomeName?: string | null;
  Odd: string | number | null;
  Timestamp: string;
  FeedType?: number;
  Outright?: boolean;
  Custom?: boolean;
  /** Nested legs when `Custom` is true. */
  Selections?: BetslipCustomSelection[];
  Blocked?: boolean;
  ProposedOdd?: number;
}

export interface BetslipItem {
  Event: BetslipEvent;
  Bets: BetslipBet[];
}

export interface BetslipTicket {
  Items: BetslipItem[];
  Rewards: PlayerReward[];
  /** Applied FreeBet / BackupBet id from `MachineBonusesActive` (UsersAPI). */
  RewardId?: number | null;
  Stake: number;
  StakePerBet?: number;
  Type: TicketType;
  /**
   * User-picked ticket type from the mode tabs. When unset, the effective type
   * follows auto rules (single for 1 selection, combi for 2+).
   */
  SelectedType?: TicketType;
  /** Chosen fold size `k` for system bets (`k/n`). */
  SystemMin?: number;
  /** Unbanked selection count `n` for system bets (`k/n`). */
  SystemMax?: number;
  Odds?: number;
  Tips?: number;
  Rows?: number;
  Combinations?: number;
  PossibleWin?: number;
  PossibleWinPending?: boolean;
  Live?: boolean;
  Changed?: boolean;
  User?: string;
  CurrencyCode?: string;
  Language?: string;
  /** Open ticket number used as KreditBet creditor (stake against its possible win). */
  CreditorNumber?: string;
  /** Creditor ticket type (`TicketType`) for connected UI. */
  CreditorType?: number;
  /** Creditor possible win — max stake while KreditBet is connected. */
  CreditorPossibleWin?: number;
}

/** Input for adding/toggling a straight selection from an odds button. */
export interface BetslipSelectionDraft {
  eventId: string | number;
  home: string;
  away: string;
  homeScore?: string;
  awayScore?: string;
  date?: string;
  live?: boolean;
  outright?: boolean;
  /** Season row id sent as `Event.SeasonId` when `outright` is true. */
  seasonId?: number;
  /** Display-only season name (not sent as a server field that survives place-bet). */
  seasonName?: string;
  category?: string;
  tournament?: string;
  tournamentId?: string | number;
  sport?: string;
  sportId?: string | number;
  marketId: string;
  market: string;
  specifier?: string | null;
  outcome: string;
  outcomeId?: string;
  outcomeName?: string;
  odd: number;
  feedType?: number;
}

export interface BetslipToggleOptions {
  disableMultiCombo?: boolean;
  maxCombi?: number;
  /** BetSettings.MinimumSystem — min selections / fold floor. */
  minSystem?: number;
  /** BetSettings.MaximumSystem — max selections for system mode. */
  maxSystem?: number;
}

/** Resolved system limits from BetSettings. */
export interface BetslipSystemLimits {
  minSystem: number;
  maxSystem: number;
}

export type BetslipToggleResult =
  | { ok: true; ticket: BetslipTicket }
  | { ok: false; reason: 'outright_mismatch' | 'max_combi' | 'custom_mixed' };

export interface BetslipSelectionKeyParts {
  eventId: string | number;
  marketId: string | null | undefined;
  specifier?: string | null;
  outcome: string | null | undefined;
}

/** Stable identity for toggle / remove / selected-odds matching. */
export function betslipSelectionKey(parts: BetslipSelectionKeyParts): string {
  const specifier = parts.specifier ?? '';
  return `${parts.eventId}|${parts.marketId ?? ''}|${specifier}|${parts.outcome ?? ''}`;
}

export function modeToTicketType(mode: BetslipMode): TicketType {
  switch (mode) {
    case 'combi':
      return TicketType.Combi;
    case 'system':
      return TicketType.System;
    default:
      return TicketType.Single;
  }
}

export function ticketTypeToMode(type: TicketType | undefined): BetslipMode {
  switch (type) {
    case TicketType.Combi:
      return 'combi';
    case TicketType.System:
      return 'system';
    default:
      return 'single';
  }
}

export function createEmptyTicket(stake = DEFAULT_BETSLIP_STAKE): BetslipTicket {
  return {
    Items: [],
    Rewards: [],
    Stake: stake,
    Type: TicketType.Single,
    SelectedType: undefined,
    Odds: 0,
    Tips: 0,
    Rows: 0,
    PossibleWin: 0,
    Changed: false,
  };
}

/**
 * Effective ticket type: 1 selection → Single; 2+ → Combi unless the user
 * explicitly picked Single or System via the mode tabs.
 */
export function resolveEffectiveTicketType(
  ticket: BetslipTicket,
  limits?: Partial<BetslipSystemLimits>,
): TicketType {
  const selections = countSystemSelections(ticket);
  if (selections <= 1) return TicketType.Single;

  if (ticket.SelectedType === TicketType.Single) return TicketType.Single;
  if (ticket.SelectedType === TicketType.System && canUseSystemMode(ticket, limits)) {
    return TicketType.System;
  }
  return TicketType.Combi;
}

/** Apply auto type rules after add/remove, then recalculate. */
export function applyTicketTypeRules(
  ticket: BetslipTicket,
  limits?: Partial<BetslipSystemLimits>,
): BetslipTicket {
  const next = cloneTicket(ticket);
  const resolved = normalizeSystemLimits(limits);
  next.Type = resolveEffectiveTicketType(next, resolved);
  if (next.Type === TicketType.System) {
    applySystemParameters(next, resolved);
  } else {
    next.SystemMin = undefined;
    next.SystemMax = undefined;
  }
  return calculateTicket(next, resolved);
}

/** Event count used for system min/max gates (legacy Items.length). */
export function countSystemSelections(ticket: BetslipTicket): number {
  return ticket.Items.length;
}

export function normalizeSystemLimits(
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipSystemLimits {
  const minRaw = limits?.minSystem;
  const maxRaw = limits?.maxSystem;
  const minSystem =
    typeof minRaw === 'number' && Number.isFinite(minRaw) && minRaw >= 1
      ? Math.floor(minRaw)
      : DEFAULT_MIN_SYSTEM;
  const maxSystem =
    typeof maxRaw === 'number' && Number.isFinite(maxRaw) && maxRaw >= minSystem
      ? Math.floor(maxRaw)
      : Math.max(DEFAULT_MAX_SYSTEM, minSystem);
  return { minSystem, maxSystem };
}

/** True when selection count is within BetSettings MinimumSystem…MaximumSystem. */
export function canUseSystemMode(
  ticket: BetslipTicket,
  limits?: Partial<BetslipSystemLimits> | null,
): boolean {
  const { minSystem, maxSystem } = normalizeSystemLimits(limits);
  const count = countSystemSelections(ticket);
  return count >= minSystem && count <= maxSystem;
}

/** Count items that are not marked as bankers. */
export function countUnbankedItems(ticket: BetslipTicket): number {
  return ticket.Items.filter((item) => !item.Event.Banked).length;
}

/**
 * Recalculate SystemMax from unbanked count and clamp SystemMin.
 * Default SystemMin is `n - 1` (e.g. 2/3, 3/4, 4/5), floored by MinimumSystem.
 */
export function applySystemParameters(
  ticket: BetslipTicket,
  limits?: Partial<BetslipSystemLimits> | null,
): void {
  const { minSystem } = normalizeSystemLimits(limits);
  const unbanked = countUnbankedItems(ticket);
  ticket.SystemMax = unbanked;

  const options = systemOptionValues(unbanked, { minSystem });
  if (options.length === 0) {
    ticket.SystemMin = unbanked > 0 ? Math.min(minSystem, unbanked) : 0;
    return;
  }

  const floor = options[0]!;
  const ceiling = options[options.length - 1]!;
  const current = ticket.SystemMin;
  if (current === undefined || current < floor || current > ceiling) {
    ticket.SystemMin = ceiling;
  }
}

/**
 * Available `k` values for the system type dropdown.
 * Uses BetSettings.MinimumSystem as the fold floor; excludes full `n/n` unless
 * that is the only valid option (`n === minSystem`).
 */
export function systemOptionValues(
  systemMax: number,
  limits?: Partial<BetslipSystemLimits> | null,
): number[] {
  const { minSystem } = normalizeSystemLimits(limits);
  if (systemMax < 1) return [];
  if (systemMax < minSystem) return [];
  if (systemMax === minSystem) return [systemMax];

  const maxK = systemMax - 1;
  const minK = Math.max(1, minSystem);
  if (minK > maxK) return [];
  return Array.from({ length: maxK - minK + 1 }, (_, i) => minK + i);
}

/** Product of all bet odds on an item (one factor per event for system math). */
function itemOddFactor(item: BetslipItem): number {
  return item.Bets.reduce((product, bet) => product * getBetOddFactor(bet), 1);
}

/**
 * All unordered subsets of `set` with exactly `count` elements (n-choose-k).
 * Ported from legacy / SportsbookApp-SN utilities.
 */
export function getCombinationSets<T>(set: T[], count: number): T[][] {
  if (count > set.length || count <= 0) return [];
  if (count === set.length) return [set.slice()];
  if (count === 1) return set.map((value) => [value]);

  const combs: T[][] = [];
  for (let i = 0; i < set.length - count + 1; i += 1) {
    const head = set.slice(i, i + 1);
    const tailCombs = getCombinationSets(set.slice(i + 1), count - 1);
    for (const tail of tailCombs) {
      combs.push(head.concat(tail));
    }
  }
  return combs;
}

/** Binomial coefficient C(max, min). */
export function binomialCount(max: number, min: number): number {
  let k = min;
  if (k > max - k) k = max - k;
  let combs = 1;
  for (let i = 1; i <= k; i += 1) {
    combs *= max - (k - i);
    combs = Math.round(combs / i);
  }
  return combs;
}

/**
 * Set the system fold size `k` (`SystemMin`). Clamped to valid option values.
 */
export function setSystemMin(
  ticket: BetslipTicket,
  systemMin: number,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const resolved = normalizeSystemLimits(limits);
  const next = cloneTicket(ticket);
  applySystemParameters(next, resolved);
  const options = systemOptionValues(next.SystemMax ?? 0, resolved);
  if (options.length === 0) return calculateTicket(next, resolved);
  const clamped = options.includes(systemMin)
    ? systemMin
    : options.reduce((best, value) =>
        Math.abs(value - systemMin) < Math.abs(best - systemMin) ? value : best,
      );
  next.SystemMin = clamped;
  return calculateTicket(next, resolved);
}

/**
 * Toggle banker on an event. Banking is blocked when it would leave fewer than
 * `MinimumSystem + 1` unbanked items (SN / BetSettings rule).
 */
export function toggleBanked(
  ticket: BetslipTicket,
  eventId: string | number,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const resolved = normalizeSystemLimits(limits);
  const next = cloneTicket(ticket);
  const item = next.Items.find((entry) => eventIdsEqual(entry.Event.EventId, eventId));
  if (!item) return ticket;

  if (item.Event.Banked) {
    item.Event.Banked = false;
  } else {
    if (!canBankEvent(next, eventId, resolved)) return ticket;
    item.Event.Banked = true;
  }

  applySystemParameters(next, resolved);
  return calculateTicket(next, resolved);
}

/** Whether banking this event is allowed (already banked can always be cleared). */
export function canBankEvent(
  ticket: BetslipTicket,
  eventId: string | number,
  limits?: Partial<BetslipSystemLimits> | null,
): boolean {
  const item = ticket.Items.find((entry) => eventIdsEqual(entry.Event.EventId, eventId));
  if (!item) return false;
  if (item.Event.Banked) return true;
  const { minSystem } = normalizeSystemLimits(limits);
  // Must leave at least minSystem + 1 unbanked so k/n options stay valid.
  return countUnbankedItems(ticket) > minSystem + 1;
}

export function countBets(ticket: BetslipTicket): number {
  return ticket.Items.reduce((sum, item) => sum + item.Bets.length, 0);
}

function eventIdsEqual(a: string | number, b: string | number): boolean {
  return String(a) === String(b);
}

function sameBet(a: BetslipBet, b: Pick<BetslipBet, 'MarketId' | 'Specifier' | 'Outcome'>): boolean {
  return (
    a.MarketId === b.MarketId &&
    (a.Specifier ?? undefined) === (b.Specifier ?? undefined) &&
    a.Outcome === b.Outcome
  );
}

function formatOdd(value: number): string {
  return formatOddForDisplay(value);
}

function nowTimestamp(): string {
  const d = new Date();
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

export function draftToItem(draft: BetslipSelectionDraft): BetslipItem {
  const homeScore = draft.homeScore ?? '';
  const awayScore = draft.awayScore ?? '';
  return {
    Event: {
      EventId: draft.eventId,
      Home: draft.home,
      Away: draft.away,
      HomeScore: homeScore,
      AwayScore: awayScore,
      Score: homeScore || awayScore ? `${homeScore}:${awayScore}` : undefined,
      Date: draft.date,
      Live: draft.live ?? false,
      Banked: false,
      Category: draft.category,
      Tournament: draft.tournament,
      TournamentId: draft.tournamentId,
      SeasonId: draft.seasonId,
      SeasonName: draft.seasonName,
      Outright: draft.outright ?? false,
      Sport: draft.sport,
      SportId: draft.sportId,
    },
    Bets: [
      {
        MarketId: draft.marketId,
        Market: draft.market,
        Specifier: draft.specifier ?? undefined,
        Outcome: draft.outcome,
        OutcomeId: draft.outcomeId ?? draft.outcome,
        OutcomeName: draft.outcomeName,
        Odd: formatOdd(draft.odd),
        Timestamp: nowTimestamp(),
        FeedType: draft.feedType,
        Outright: draft.outright ?? false,
      },
    ],
  };
}

function cloneTicket(ticket: BetslipTicket): BetslipTicket {
  return structuredClone(ticket);
}

function hasCustomOnEvent(ticket: BetslipTicket, eventId: string | number): boolean {
  return ticket.Items.some(
    (item) =>
      eventIdsEqual(item.Event.EventId, eventId) && item.Bets.some((bet) => bet.Custom === true),
  );
}

/**
 * Toggle a selection onto/off the ticket (legacy `Betting.Bet` core rules).
 */
export function toggleSelection(
  ticket: BetslipTicket,
  draft: BetslipSelectionDraft,
  options: BetslipToggleOptions = {},
): BetslipToggleResult {
  const maxCombi = options.maxCombi ?? DEFAULT_MAX_COMBI;
  const disableMultiCombo = options.disableMultiCombo ?? false;
  const next = ticket.Items.length === 0 ? createEmptyTicket(ticket.Stake || DEFAULT_BETSLIP_STAKE) : cloneTicket(ticket);
  const incoming = draftToItem(draft);
  const incomingBet = incoming.Bets[0];
  if (!incomingBet) {
    return { ok: false, reason: 'max_combi' };
  }
  if (hasCustomOnEvent(next, draft.eventId)) {
    return { ok: false, reason: 'custom_mixed' };
  }

  for (const item of next.Items) {
    if (Boolean(item.Event.Outright) !== Boolean(draft.outright)) {
      return { ok: false, reason: 'outright_mismatch' };
    }
  }

  const eventIndex = next.Items.findIndex((item) =>
    eventIdsEqual(item.Event.EventId, draft.eventId),
  );

  if (eventIndex >= 0) {
    const item = next.Items[eventIndex]!;
    const betIndex = item.Bets.findIndex((bet) => sameBet(bet, incomingBet));

    if (betIndex >= 0) {
      item.Bets.splice(betIndex, 1);
      if (item.Bets.length === 0) next.Items.splice(eventIndex, 1);
    } else {
      if (disableMultiCombo) {
        item.Bets = [];
      }
      if (countBets(next) >= maxCombi) {
        return { ok: false, reason: 'max_combi' };
      }
      item.Bets.push(incomingBet);
    }
  } else {
    if (countBets(next) >= maxCombi) {
      return { ok: false, reason: 'max_combi' };
    }
    next.Items.push(incoming);
  }

  next.Changed = false;
  return {
    ok: true,
    ticket: applyTicketTypeRules(next, {
      minSystem: options.minSystem,
      maxSystem: options.maxSystem,
    }),
  };
}

export function removeSelection(
  ticket: BetslipTicket,
  key: BetslipSelectionKeyParts,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const next = cloneTicket(ticket);
  const eventIndex = next.Items.findIndex((item) =>
    eventIdsEqual(item.Event.EventId, key.eventId),
  );
  if (eventIndex < 0) return ticket;

  const item = next.Items[eventIndex]!;
  const betIndex = item.Bets.findIndex((bet) =>
    sameBet(bet, {
      MarketId: key.marketId ?? null,
      Specifier: key.specifier,
      Outcome: key.outcome ?? null,
    }),
  );
  if (betIndex < 0) return ticket;

  item.Bets.splice(betIndex, 1);
  if (item.Bets.length === 0) next.Items.splice(eventIndex, 1);

  return applyTicketTypeRules(next, limits ?? undefined);
}

export function clearTicket(stake = DEFAULT_BETSLIP_STAKE): BetslipTicket {
  return createEmptyTicket(stake);
}

export function setStake(
  ticket: BetslipTicket,
  stake: number,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const safe = Number.isFinite(stake) && stake >= 0 ? stake : 0;
  return calculateTicket({ ...cloneTicket(ticket), Stake: safe }, limits);
}

export function setMode(
  ticket: BetslipTicket,
  mode: BetslipMode,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const resolved = normalizeSystemLimits(limits);
  if (mode === 'system' && !canUseSystemMode(ticket, resolved)) {
    return ticket;
  }
  const type = modeToTicketType(mode);
  // SelectedType locks the user's choice so auto-combi does not override it.
  return applyTicketTypeRules(
    {
      ...cloneTicket(ticket),
      Type: type,
      SelectedType: type,
    },
    resolved,
  );
}

export function getBetOddFactor(bet: BetslipBet): number {
  // Legacy: pending Custom odds must not zero the whole ticket product.
  if (
    bet.Custom &&
    (bet.Odd === null || bet.Odd === undefined || bet.Odd === '')
  ) {
    return 1;
  }
  const raw = bet.Odd;
  const value = typeof raw === 'number' ? raw : Number.parseFloat(String(raw ?? ''));
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function cartesianProduct(groups: number[][]): number[][] {
  if (groups.length === 0) return [[]];
  return groups.reduce<number[][]>(
    (acc, group) => acc.flatMap((prefix) => group.map((value) => [...prefix, value])),
    [[]],
  );
}

function calculateSingle(ticket: BetslipTicket): void {
  let oddsSum = 0;
  let tips = 0;
  for (const item of ticket.Items) {
    for (const bet of item.Bets) {
      tips += 1;
      oddsSum += getBetOddFactor(bet);
    }
  }
  ticket.Tips = tips;
  ticket.Rows = tips;
  ticket.Odds = tips > 0 ? oddsSum / tips : 0;
  ticket.PossibleWin = ticket.Stake * (ticket.Odds ?? 0);
  ticket.PossibleWinPending = false;
  if (tips > 0) {
    ticket.StakePerBet = ticket.Stake / tips;
  }
}

function calculateCombi(ticket: BetslipTicket): void {
  let odds = 1;
  let tips = 0;
  let rows = 1;

  for (const item of ticket.Items) {
    let itemRows = 0;
    for (const bet of item.Bets) {
      itemRows += 1;
      tips += 1;
      odds *= getBetOddFactor(bet);
    }
    rows *= Math.max(itemRows, 1);
  }

  if (rows === 1) {
    ticket.Odds = odds;
    ticket.Tips = tips;
    ticket.Rows = rows;
    ticket.PossibleWin = ticket.Stake * odds;
  } else {
    const combiOdds = ticket.Items.map((item) => item.Bets.map((bet) => getBetOddFactor(bet)));
    const combinations = cartesianProduct(combiOdds);
    let ticketOdds = 0;
    for (const combo of combinations) {
      ticketOdds += combo.reduce((product, value) => product * value, 1);
    }
    ticket.Odds = combinations.length > 0 ? ticketOdds / combinations.length : 0;
    ticket.Tips = tips;
    ticket.Rows = combinations.length;
    ticket.Combinations = combinations.length;
    ticket.PossibleWin = ticket.Stake * (ticket.Odds ?? 0);
  }
  ticket.PossibleWinPending = false;
}

function calculateSystem(
  ticket: BetslipTicket,
  limits?: Partial<BetslipSystemLimits> | null,
): void {
  applySystemParameters(ticket, limits);
  const systemMin = ticket.SystemMin ?? 0;
  const unbankedOdds: number[] = [];
  let bankedOdd = 1;

  for (const item of ticket.Items) {
    const factor = itemOddFactor(item);
    if (item.Event.Banked) {
      bankedOdd *= factor;
    } else {
      unbankedOdds.push(factor);
    }
  }

  if (
    unbankedOdds.length < 1 ||
    systemMin < 1 ||
    systemMin > unbankedOdds.length
  ) {
    ticket.Odds = 0;
    ticket.Tips = 0;
    ticket.Rows = ticket.Items.length;
    ticket.Combinations = 0;
    ticket.PossibleWin = 0;
    ticket.PossibleWinPending = false;
    return;
  }

  const combinations = getCombinationSets(unbankedOdds, systemMin);
  let systemOdd = 0;
  for (const combo of combinations) {
    systemOdd += combo.reduce((product, value) => product * value, 1);
  }

  ticket.Combinations = combinations.length;
  ticket.Tips = combinations.length;
  ticket.Rows = ticket.Items.length;
  ticket.Odds =
    combinations.length > 0 ? (bankedOdd * systemOdd) / combinations.length : 0;
  ticket.PossibleWin = ticket.Stake * (ticket.Odds ?? 0);
  ticket.PossibleWinPending = false;
  if (combinations.length > 0) {
    ticket.StakePerBet = ticket.Stake / combinations.length;
  }
}

/**
 * Recalculate Odds / PossibleWin for Single, Combi, or System.
 */
function ticketHasPendingCustomOdd(ticket: BetslipTicket): boolean {
  for (const item of ticket.Items) {
    for (const bet of item.Bets) {
      if (!bet.Custom) continue;
      if (bet.Odd === null || bet.Odd === undefined || bet.Odd === '') return true;
    }
  }
  return false;
}

export function calculateTicket(
  ticket: BetslipTicket,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const next = cloneTicket(ticket);
  const type = next.Type ?? TicketType.Single;

  if (countBets(next) === 0) {
    next.Odds = 0;
    next.Tips = 0;
    next.Rows = 0;
    next.PossibleWin = 0;
    next.PossibleWinPending = false;
    next.Combinations = undefined;
    next.SystemMin = undefined;
    next.SystemMax = undefined;
    return next;
  }

  if (type === TicketType.Single) {
    calculateSingle(next);
  } else if (type === TicketType.System) {
    calculateSystem(next, limits);
  } else {
    calculateCombi(next);
  }

  next.Live = next.Items.some(
    (item) => item.Event.Live === true || item.Event.Live === 'true' || item.Event.Live === '1',
  );
  next.PossibleWinPending = ticketHasPendingCustomOdd(next);

  return next;
}

export function isSelectionInTicket(
  ticket: BetslipTicket,
  parts: BetslipSelectionKeyParts,
): boolean {
  const key = betslipSelectionKey(parts);
  for (const item of ticket.Items) {
    for (const bet of item.Bets) {
      if (
        betslipSelectionKey({
          eventId: item.Event.EventId,
          marketId: bet.MarketId,
          specifier: bet.Specifier,
          outcome: bet.Outcome,
        }) === key
      ) {
        return true;
      }
    }
  }
  return false;
}

export function parseStoredTicket(raw: string | null): BetslipTicket | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<BetslipTicket>;
    if (!parsed || !Array.isArray(parsed.Items)) return null;
    const stake =
      typeof parsed.Stake === 'number' && Number.isFinite(parsed.Stake)
        ? parsed.Stake
        : DEFAULT_BETSLIP_STAKE;
    const type =
      parsed.Type === TicketType.Combi ||
      parsed.Type === TicketType.System ||
      parsed.Type === TicketType.Single
        ? parsed.Type
        : TicketType.Single;
    const rewardId =
      typeof parsed.RewardId === 'number' && Number.isFinite(parsed.RewardId)
        ? parsed.RewardId
        : parsed.RewardId === null
          ? null
          : undefined;
    const rewards = Array.isArray(parsed.Rewards)
      ? (parsed.Rewards as PlayerReward[]).filter(
          (r) => r && typeof r.Id === 'number' && typeof r.Type === 'number',
        )
      : [];
    return applyTicketTypeRules({
      Items: parsed.Items,
      Rewards: rewards,
      RewardId: rewardId,
      Stake: stake,
      Type: type,
      SelectedType:
        parsed.SelectedType === TicketType.Combi ||
        parsed.SelectedType === TicketType.System ||
        parsed.SelectedType === TicketType.Single
          ? parsed.SelectedType
          : undefined,
      SystemMin:
        typeof parsed.SystemMin === 'number' && Number.isFinite(parsed.SystemMin)
          ? parsed.SystemMin
          : undefined,
      SystemMax:
        typeof parsed.SystemMax === 'number' && Number.isFinite(parsed.SystemMax)
          ? parsed.SystemMax
          : undefined,
      Changed: Boolean(parsed.Changed),
      CurrencyCode: parsed.CurrencyCode,
      Language: parsed.Language,
      User: parsed.User,
      CreditorNumber:
        typeof parsed.CreditorNumber === 'string' && parsed.CreditorNumber.trim()
          ? parsed.CreditorNumber.trim()
          : undefined,
      CreditorType:
        typeof parsed.CreditorType === 'number' && Number.isFinite(parsed.CreditorType)
          ? parsed.CreditorType
          : undefined,
      CreditorPossibleWin:
        typeof parsed.CreditorPossibleWin === 'number' &&
        Number.isFinite(parsed.CreditorPossibleWin)
          ? parsed.CreditorPossibleWin
          : undefined,
    });
  } catch {
    return null;
  }
}

export interface FlattenedBetslipCustomLeg {
  key: string;
  marketLabel: string;
  outcomeName: string;
  specifier?: string | null;
}

export interface FlattenedBetslipSelection {
  key: string;
  eventId: string | number;
  marketId: string | null;
  specifier?: string | null;
  outcome: string | null;
  outcomeName: string;
  matchLabel: string;
  marketLabel: string;
  odds: string;
  banked: boolean;
  blocked?: boolean;
  live?: boolean;
  homeScore?: string;
  awayScore?: string;
  clock?: string;
  liveClockState?: LiveClockState;
  /** True when this row is a season outright (labels must not append the markettext specifier). */
  outright?: boolean;
  /** True when this row is a Bet Builder package. */
  custom?: boolean;
  /** Nested BB legs when `custom` is true. */
  customLegs?: FlattenedBetslipCustomLeg[];
  /** Selection count inside a Custom package (may be below min until priced). */
  customSelectionCount?: number;
}

/** Flatten items into display rows for the betslip UI. */
export function flattenBetslipSelections(ticket: BetslipTicket): FlattenedBetslipSelection[] {
  const rows: FlattenedBetslipSelection[] = [];

  for (const item of ticket.Items) {
    const home = item.Event.Home || '';
    const away = item.Event.Away || '';
    const seasonLabel = item.Event.SeasonName || (item.Event.Outright ? away : '');
    const matchLabel = item.Event.Outright
      ? [home, seasonLabel].filter(Boolean).join(' · ')
      : !away
        ? home
        : `${home} vs ${away}`;
    const live =
      item.Event.Live === true ||
      item.Event.Live === 'true' ||
      item.Event.Live === '1';
    const homeScore =
      item.Event.HomeScore != null
        ? String(item.Event.HomeScore).trim() || undefined
        : undefined;
    const awayScore =
      item.Event.AwayScore != null
        ? String(item.Event.AwayScore).trim() || undefined
        : undefined;

    // Resolve clock state from raw period/time so the UI can translate period codes.
    const liveInfo = live
      ? resolveBetslipEventLiveInfo(item.Event)
      : undefined;
    const clock = liveInfo?.clock;
    const liveClockState = liveInfo?.liveClockState;

    for (const bet of item.Bets) {
      if (bet.Custom) {
        const legs: FlattenedBetslipCustomLeg[] = (bet.Selections ?? []).map((sel) => {
          const spec = sel.Specifier?.trim();
          const baseName = sel.OutcomeName?.trim() || sel.Outcome || '';
          const outcomeName =
            spec && !baseName.includes('(') ? `${baseName} (${displaySpec(spec)})` : baseName;
          return {
            key: `${sel.MarketId ?? ''}|${sel.Outcome ?? sel.OutcomeId ?? ''}|${sel.Specifier ?? ''}`,
            marketLabel: sel.Market ?? '',
            outcomeName,
            specifier: sel.Specifier,
          };
        });
        const oddFactor = getBetOddFactor(bet);
        rows.push({
          key: `custom|${item.Event.EventId}`,
          eventId: item.Event.EventId,
          marketId: null,
          outcome: null,
          outcomeName: 'Bet Builder',
          matchLabel,
          marketLabel: 'BB',
          odds: oddFactor > 0 ? formatDisplayOdd(oddFactor) : '—',
          banked: Boolean(item.Event.Banked),
          blocked: bet.Blocked,
          live,
          homeScore,
          awayScore,
          clock,
          liveClockState,
          custom: true,
          customLegs: legs,
          customSelectionCount: legs.length,
        });
        continue;
      }

      const specifier = bet.Specifier?.trim();
      const outright = Boolean(item.Event.Outright);
      const baseName = bet.OutcomeName?.trim() || bet.Outcome || '';
      const outcomeName =
        outright || !specifier || baseName.includes('(')
          ? baseName
          : `${baseName} (${displaySpec(specifier)})`;
      rows.push({
        key: betslipSelectionKey({
          eventId: item.Event.EventId,
          marketId: bet.MarketId,
          specifier: bet.Specifier,
          outcome: bet.Outcome,
        }),
        eventId: item.Event.EventId,
        marketId: bet.MarketId,
        specifier: bet.Specifier,
        outcome: bet.Outcome,
        outcomeName,
        outright,
        matchLabel,
        marketLabel: outright && specifier ? specifier : (bet.Market ?? ''),
        odds: formatDisplayOdd(getBetOddFactor(bet)),
        banked: Boolean(item.Event.Banked),
        blocked: bet.Blocked,
        live,
        homeScore,
        awayScore,
        clock,
        liveClockState,
      });
    }
  }

  return rows;
}

function resolveBetslipEventLiveInfo(event: BetslipEvent) {
  const stats: Record<string, unknown> = {};
  if (event.TimeExtended) {
    stats.CurrentTimeExtended = event.TimeExtended;
    stats.MatchTimeExtended = event.TimeExtended;
    stats.matchTimeExtended = event.TimeExtended;
  }
  if (event.PeriodShort) {
    stats.CurrentPartShort = event.PeriodShort;
    stats.currentPartShort = event.PeriodShort;
  }
  if (event.Period) {
    stats.CurrentPart = event.Period;
    stats.currentPart = event.Period;
  }
  if (event.Time) {
    stats.CurrentTime = event.Time;
    stats.currentTime = event.Time;
  }

  return resolveEventRowLiveInfo({
    HomeScore: event.HomeScore != null ? Number(event.HomeScore) : undefined,
    AwayScore: event.AwayScore != null ? Number(event.AwayScore) : undefined,
    Time: event.Time ?? null,
    Period: event.Period ?? null,
    PeriodShort: event.PeriodShort ?? null,
    Statistics: Object.keys(stats).length > 0 ? JSON.stringify(stats) : null,
  });
}

function displaySpec(specifier: string): string {
  const eq = specifier.indexOf('=');
  return eq >= 0 ? specifier.slice(eq + 1) : specifier;
}

function formatDisplayOdd(value: number): string {
  return formatOddForDisplay(value);
}
